import Card from '@/components/Card';
import { FieldValues } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { CopyIcon } from '@/components/SvgIcons/CopyIcon';
import { StatusTag } from '@/components/Tag/StatusTag';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { API_BASE_URL } from '@/utils/constant.utils';

export const GroupWhiteLabelAppManagement = <TSchema extends FieldValues>({
  t,
}: FormManagementProps<TSchema>) => {
  const { id } = useParams();
  const qrCodeLink = `${API_BASE_URL}/group/deeplink/${id}`;
  const [linkIsCopied, setLinkIsCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(qrCodeLink);
    setLinkIsCopied(true);
    setTimeout(() => {
      setLinkIsCopied(false);
    }, 400);
  };

  return (
    <Card
      title={
        <TitleSection
          text={t('common:appQRCode')}
          defaultIconClassName="ki-double-check-circle"
        />
      }
      cardClassName="mb-5"
      bodyClassName="flex flex-col gap-5"
    >
      <div className="flex items-center gap-2">
        <div>
          <span className="text-sm font-semibold">
            {t('common:qrCodeLink')} :
          </span>{' '}
          <span className="text-[13.3px] text-slate-500/75 px-2.5 py-1 bg-slate-100/40 rounded-[4px]">
            {qrCodeLink}
          </span>
        </div>
        <div
          className={twMerge(
            'mt-0.5 relative flex items-center justify-center p-2 bg-slate-100 rounded-full hover:cursor-pointer after:content-[""] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-0 after:h-0 after:bg-purple-500/5 after:rounded-full after:transition-all after:duration-200 after:ease-in-out',
            linkIsCopied && 'after:w-full after:h-full'
          )}
          onClick={handleCopyLink}
        >
          {linkIsCopied && (
            <StatusTag
              className="absolute -top-full left-[50%] -translate-x-1/2 py-0.5 px-1.5 bg-purple-F6EEFE border-purple-200 text-purple-500 text-[10.5px] font-medium tracking-wide"
              text={
                <div className="flex items-center gap-0.5">
                  <i className="ki-outline ki-check-circle text-[14px] text-purple-500" />
                  {t('common:copied')}
                </div>
              }
            />
          )}
          <CopyIcon className="w-3.5 h-3.5 [&>path]:fill-gray-700" />
        </div>
      </div>
      <div className="flex justify-center items-center pt-[55px] pb-[86.5px]">
        <div className="bg-white rounded-lg shadow-sm">
          <QRCodeSVG
            value={qrCodeLink}
            size={208}
            bgColor="#FFFFFF"
            fgColor="#000000"
            level="M"
            includeMargin={false}
          />
        </div>
      </div>
    </Card>
  );
};
